import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { formatCount } from '@/lib/numbers';

import type { DailyPoint } from './dashboardData';
import { formatDay } from './dateRange';
import { describeTrend } from './describeTrend';

/** The whole panel's height, so the loading placeholder and the empty message match it. */
export const TREND_HEIGHT = 200;
const LINE_HEIGHT = 140;

interface TrendLineProps {
  /** The category's name, as its tab says it. */
  label: string;
  points: DailyPoint[];
}

/**
 * The daily series as a plain line, until the chart library arrives on Day 80.
 *
 * No axis, no hover, no legend: those come with the real chart, when the
 * numbers are worth hovering over. The picture has a text alternative, so it
 * means the same without being seen, and the labels drawn around it are
 * hidden from a screen reader because that alternative already says them.
 */
export function TrendLine({ label, points }: TrendLineProps) {
  const max = Math.max(0, ...points.map((point) => point.value));

  if (points.length === 0 || max === 0) {
    return (
      <Box
        sx={{
          height: TREND_HEIGHT,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography color="text.secondary">No activity in this period</Typography>
      </Box>
    );
  }

  // A single day is drawn as a level line across the box rather than a dot.
  const values =
    points.length === 1 ? [points[0].value, points[0].value] : points.map((p) => p.value);
  const width = values.length - 1;
  // A little room above and below, so the line is not cut in half at the edges.
  const margin = max * 0.05;
  const coordinates = values.map((value, index) => `${index},${max - value}`).join(' ');
  const highest = points.reduce((top, point) => (point.value > top.value ? point : top));

  return (
    <Box
      sx={{ height: TREND_HEIGHT, display: 'flex', flexDirection: 'column', minWidth: 0 }}
    >
      <Typography variant="body2" color="text.secondary" aria-hidden="true">
        Highest: {formatCount(highest.value)} on {formatDay(highest.date)}
      </Typography>
      <Box
        component="svg"
        role="img"
        aria-label={describeTrend(label, points)}
        viewBox={`0 ${-margin} ${width} ${max + margin * 2}`}
        // Stretched to the box, so the line fills whatever width the card has.
        preserveAspectRatio="none"
        width="100%"
        height={LINE_HEIGHT}
        sx={{ display: 'block', my: 1, color: 'primary.main', overflow: 'visible' }}
      >
        <polyline
          points={coordinates}
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinejoin="round"
          // Two pixels wide at any stretch, rather than scaled with the box.
          vectorEffect="non-scaling-stroke"
        />
      </Box>
      <Box
        aria-hidden="true"
        sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}
      >
        <Typography variant="body2" color="text.secondary">
          {formatDay(points[0].date)}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {formatDay(points[points.length - 1].date)}
        </Typography>
      </Box>
    </Box>
  );
}
